import { useEffect, useState } from "react";
import { api } from "../lib/api";
import BookingForm from "../components/BookingForm";
import { Calendar, Users, MapPin, Tag } from "lucide-react";

export default function Workshops() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/workshops").then((r) => setList(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="workshops-page" className="bg-ink">
      <section className="container-x section-y">
        <p className="overline">Studio diary</p>
        <h1 className="mt-4 font-display text-6xl md:text-8xl text-white">
          Upcoming<br /><span className="text-rose">workshops.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-white/75 text-lg">
          Small groups. Generous time. Beautiful materials. Pick a date below or send a
          custom enquiry — group bookings and weekday slots can be arranged.
        </p>

        <div className="mt-16">
          {loading ? (
            <p className="text-white/60">Loading workshops…</p>
          ) : list.length === 0 ? (
            <p className="text-white/60">No workshops scheduled — please use the form below to enquire.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="workshops-grid">
              {list.map((w) => (
                <article key={w.id} data-testid={`workshop-card-${w.id}`} className="card-brutal flex flex-col relative">
                  <span className="tag-sticker absolute -top-3 -left-2">{w.workshop_type.replace("_", " ")}</span>
                  <h3 className="mt-6 font-display text-3xl text-white leading-tight">{w.title}</h3>
                  <p className="mt-3 text-white/70 text-sm leading-relaxed flex-1">{w.description}</p>
                  {w.notes && <p className="mt-3 text-xs text-rose italic">{w.notes}</p>}
                  <div className="mt-6 space-y-2 text-sm text-white/75">
                    <div className="flex items-start gap-2"><Calendar size={14} className="text-rose mt-0.5 flex-shrink-0" /><span>{w.date} · {w.time}</span></div>
                    <div className="flex items-start gap-2"><MapPin size={14} className="text-rose mt-0.5 flex-shrink-0" /><span>{w.location}</span></div>
                    <div className="flex items-start gap-2"><Users size={14} className="text-rose mt-0.5 flex-shrink-0" /><span>{w.age_group} · Limited spaces</span></div>
                    <div className="flex items-start gap-2"><Tag size={14} className="text-gold mt-0.5 flex-shrink-0" /><span>{w.price_note ? w.price_note : `£${w.price.toFixed(0)}`}</span></div>
                  </div>
                  <p className="mt-5 text-[11px] uppercase tracking-[0.22em] text-gold font-bold">£10 deposit per attendee secures the booking</p>
                  <a href="#book" className="mt-4 btn-primary self-start text-xs">Book this date</a>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section id="book" className="bg-coal section-y border-t-2 border-white/10">
        <div className="container-x grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="overline">Booking & deposit</p>
            <h2 className="mt-4 font-display text-5xl md:text-6xl text-white">Reserve your spot<br /><span className="text-rose">or build your own.</span></h2>
            <p className="mt-6 text-white/75 leading-relaxed">
              Fill in the form and pay a <span className="text-gold font-bold">£10 non-refundable deposit per attendee</span> to
              secure the spot. The remaining balance is paid on the day. Suitable for
              all ages — adults, teens, parents and children all welcome. Group and
              private bookings included.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-white/75 uppercase tracking-wider">
              <li>· £10 deposit per attendee</li>
              <li>· Remaining balance paid on the day</li>
              <li>· All materials provided</li>
              <li>· Confirmation within 48 hours</li>
            </ul>
            <div className="mt-10 card-brutal">
              <p className="overline !text-gold">Booked already?</p>
              <p className="mt-2 text-sm text-white/75">Tell Miss Lolly about your vision — fill the personalised jacket questionnaire so the design is ready before your session.</p>
              <a href="/jacket-questionnaire" data-testid="jq-link" className="mt-4 btn-secondary text-xs inline-flex">Open jacket brief</a>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="card-brutal">
              <BookingForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
